<template>
  <div class="ForWhomForm">
    <h2 class="gl_form-title">
      {{ t('rater.common.for-whom.form-legend') }}
    </h2>

    <RadioButtonGroup
      id="main"
      :value="forWhom"
      :options="forWhomOptions"
      v-model="forWhom"
      @change="restartValues"
      @update:model-value="restartValues"
    >
      <template
        v-if="forWhom === ForWhomEnum.ME_MORE"
        v-slot:me-more
      >
        <div>
          <RadioButtonGroup
            id="sub-me-more"
            :key="howManyPeopleOption"
            :legend="t('rater.common.for-whom.how-many')"
            :value="howManyPeopleOption"
            :options="createSubOptions(2)"
            v-model="howManyPeopleOption"
            flex-direction="row"
            @change.stop="restartPeopleValue"
            @update:model-value="restartPeopleValue"
            :small="true"
          />

          <BaseSelect
            v-if="howManyPeopleOption === 'more'"
            id="other"
            v-model="morePeopleOption"
            :items="morePeopleOptions.slice(1)"
            icon="icon-chevron-down"
          />

          <CardInfoComponent
            v-if="isPymes && howManyPeopleOption === 'more'"
            icon="icon-info"
          >
            <p class="card-info__text">
              {{ t('rater.health.for-whom.more-than-message', { maxInsureds: maxInsurance }) }}
            </p>
            <p
              class="card-info__link"
              @click="callMeFree"
            >
              {{ t('rater.health.for-whom.contact-with-assistant') }}
            </p>
          </CardInfoComponent>
        </div>
      </template>

      <template
        v-if="forWhom === ForWhomEnum.NOT_ME"
        v-slot:not-me
      >
        <div>
          <RadioButtonGroup
            id="sub-not-me"
            :key="howManyPeopleOption"
            :legend="t('rater.common.for-whom.how-many')"
            :value="howManyPeopleOption"
            :options="createSubOptions(isPymes ? 2 : 1)"
            v-model="howManyPeopleOption"
            flex-direction="row"
            @change.stop="restartPeopleValue"
            @update:model-value="restartPeopleValue"
            :small="true"
          />

          <BaseSelect
            v-if="howManyPeopleOption === 'more'"
            id="more-people"
            v-model="morePeopleOption"
            :items="isPymes ? morePeopleOptions.slice(1) : morePeopleOptions"
          />

          <CardInfoComponent
            v-if="isPymes && howManyPeopleOption === 'more'"
            icon="icon-info"
          >
            <p class="card-info__text">
              {{ t('rater.health.for-whom.more-than-message', { maxInsureds: maxInsurance }) }}
            </p>
            <p
              class="card-info__link"
              @click="callMeFree"
            >
              {{ t('rater.health.for-whom.contact-with-assistant') }}
            </p>
          </CardInfoComponent>
        </div>
      </template>
    </RadioButtonGroup>
  </div>
</template>

<script lang="ts" setup>
  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import Insured from '@/modules/common/common/models/APP/Insured'
  import type { BaseFormAnswersInterface } from '@/modules/common/common/models/APP/types'
  import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
  import RadioButtonGroup from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import { ForWhomEnum, UserRoleEnum } from '@/modules/core/models/enums'
  import { useSupportModalStore } from '@/modules/core/stores'
  import { arrayRange } from '@/utils/array.utils'
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  const MIN_CUSTOM_PEOPLE = 3

  const { t } = useI18n()
  const supportModalStore = useSupportModalStore()

  defineExpose({ triggerSubmit })
  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})
  const isPymes = props.configurations.isPymes

  const forWhomOptions = [
    ...(isPymes
      ? []
      : [
          {
            label: t('rater.common.for-whom.options.me'),
            value: ForWhomEnum.ME
          }
        ]),
    {
      label: t('rater.common.for-whom.options.me-more'),
      value: ForWhomEnum.ME_MORE
    },
    {
      label: t('rater.common.for-whom.options.more'),
      value: ForWhomEnum.NOT_ME
    }
  ]

  const morePeopleOptions = computed(() => {
    return arrayRange(MIN_CUSTOM_PEOPLE, maxInsurance.value).map((label) => ({
      label: String(label),
      value: String(label)
    }))
  })

  const { forWhomPeople } = props.answers
  const forWhom = ref(props.answers.forWhom || forWhomOptions[0].value)

  const initialHowManyPeopleOption = calculateInitialPeople()
  const initialMorePeopleOption = forWhomPeople >= MIN_CUSTOM_PEOPLE ? forWhomPeople : 1

  const howManyPeopleOption = ref(initialHowManyPeopleOption)
  const morePeopleOption = ref(initialMorePeopleOption)

  const maxInsurance = computed(() => props.configurations.maxInsurances)
  const people = computed(() =>
    Number(
      howManyPeopleOption.value === 'more' ? morePeopleOption.value : howManyPeopleOption.value
    )
  )

  const createSubOptions = (min: number) => {
    return [
      { label: min.toString(), value: String(min) },
      { label: (min + 1).toString(), value: String(min + 1) },
      { label: t('rater.common.more'), value: 'more' }
    ]
  }

  function restartValues() {
    restartSubValues()
    restartPeopleValue()
  }

  function restartSubValues() {
    if (forWhom.value === ForWhomEnum.ME_MORE) {
      howManyPeopleOption.value = '2'
      return
    }

    howManyPeopleOption.value = isPymes ? '2' : '1'
    return
  }

  function restartPeopleValue() {
    if (howManyPeopleOption.value === 'more') {
      if (forWhom.value === ForWhomEnum.ME_MORE) {
        morePeopleOption.value = MIN_CUSTOM_PEOPLE + 1
        return
      }
      morePeopleOption.value = isPymes ? MIN_CUSTOM_PEOPLE + 1 : MIN_CUSTOM_PEOPLE
      return
    }

    morePeopleOption.value = 0
  }

  function calculateNextPage() {
    if (props.nextForm.length === 2) {
      if (forWhom.value !== ForWhomEnum.ME) {
        return props.nextForm[1]
      }
    }
    if (props.nextForm.length === 3) {
      if (forWhom.value === ForWhomEnum.ME_MORE) {
        return props.nextForm[1]
      }
      if (forWhom.value === ForWhomEnum.NOT_ME) {
        return props.nextForm[2]
      }
    }
    return props.nextForm[0]
  }

  function calculateInitialPeople(): string {
    if (forWhom.value === ForWhomEnum.ME_MORE) {
      return forWhomPeople <= MIN_CUSTOM_PEOPLE ? String(forWhomPeople) : 'more'
    }
    return forWhomPeople < (isPymes ? MIN_CUSTOM_PEOPLE + 1 : MIN_CUSTOM_PEOPLE)
      ? String(forWhomPeople)
      : 'more'
  }

  function triggerSubmit() {
    const page = calculateNextPage()

    const answers: Partial<BaseFormAnswersInterface> = {
      forWhom: forWhom.value,
      forWhomPeople: people.value
    }

    if (
      !props.answers.forWhomPeople ||
      answers.forWhomPeople !== props.answers.forWhomPeople ||
      answers.forWhom !== props.answers.forWhom
    ) {
      // reset insureds
      answers['insureds'] = getCleanInsureds()
    }

    return { page, answers }
  }

  function getCleanInsureds(currentPosition = 1, length = people.value) {
    return arrayRange(currentPosition, length).map((i) => {
      const isFirst = i === 1
      const isHolder = isFirst && forWhom.value !== ForWhomEnum.NOT_ME
      const role = isHolder ? UserRoleEnum.HOLDER_INSURED : UserRoleEnum.INSURED
      return new Insured({
        isHolder,
        role
      })
    })
  }

  function callMeFree() {
    supportModalStore.setOpenModal(true)
  }
</script>

<style lang="scss" scoped>
  .ForWhomForm {
    .RadioButtonGroupComponent {
      margin-bottom: $spacing-l;

      :deep(.ErrorMessageComponent) {
        display: none;
      }
    }

    .card-info {
      &__text {
        text-align: start;
        margin-bottom: $spacing-s;
      }

      &__link {
        text-align: start;
        font-weight: var(--font-weight-700);
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }
</style>
